import React, { useEffect, useState } from "react";
import { Loader2, Send, ShieldCheck, Building2, AlertTriangle, Upload, CheckCircle2, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import KycDocumentsPanel from "@/components/kyc/KycDocumentsPanel";
import { providerKycApi } from "@/lib/api/kyc";
import { useI18n } from "@/contexts/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";

function StatusBadge({ status, t }) {
    const tone = status === "APPROVED" ? "border-success/40 text-success bg-success/5"
              : status === "PENDING"  ? "border-warning/40 text-warning bg-warning/5"
              : status === "REJECTED" ? "border-destructive/40 text-destructive bg-destructive/5"
              : "border-border text-muted-foreground";
    return <Badge variant="outline" className={tone}>{t(`offers.status.${status}`)}</Badge>;
}

export default function ProviderKycPage() {
    const { t } = useI18n();
    const [data, setData] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const refresh = async () => {
        try { setData(await providerKycApi.summary()); }
        catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message); }
    };
    useEffect(() => { refresh(); }, []);

    const onSubmit = async () => {
        setSubmitting(true);
        try {
            await providerKycApi.submit();
            toast.success(t("kyc.toasts.submitted"));
            refresh();
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        } finally { setSubmitting(false); }
    };

    if (!data) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]" data-testid="page-provider-kyc-loading">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
        );
    }

    const p = data.provider;
    const isPending  = p.kyc_status === "PENDING";
    const isApproved = p.kyc_status === "APPROVED";
    const isDraft    = p.kyc_status === "DRAFT";
    const hasAnyDocs = data.documents && data.documents.length > 0;

    return (
        <div className="max-w-4xl space-y-6 fade-up" data-testid="page-provider-kyc">
            <header>
                <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("kyc.providerTitle")}</h1>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("kyc.providerSubtitle")}</p>
            </header>

            {/* Welcome banner — shown to new providers who haven't uploaded anything yet */}
            {isDraft && !hasAnyDocs && (
                <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
                    <div className="flex items-start gap-3">
                        <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
                        <div>
                            <p className="font-semibold text-foreground">{t("kyc.welcomeTitle")}</p>
                            <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{t("kyc.welcomeBody")}</p>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 pt-1">
                        {[
                            { icon: Upload,        step: "1", label: t("kyc.step1") },
                            { icon: Send,          step: "2", label: t("kyc.step2") },
                            { icon: CheckCircle2,  step: "3", label: t("kyc.step3") },
                        ].map(({ icon: Icon, step, label }) => (
                            <div key={step} className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3">
                                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Icon className="h-3.5 w-3.5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-xs text-muted-foreground">{t("kyc.stepLabel", { n: step })}</p>
                                    <p className="text-sm font-medium leading-tight">{label}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 shrink-0" />
                        {t("kyc.reviewTime")}
                    </p>
                </div>
            )}

            <section className="surface-card p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="icon-tile h-12 w-12 shrink-0">
                    <Building2 className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-base font-semibold truncate">{p.corporate_name}</p>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                        <StatusBadge status={p.kyc_status} t={t} />
                        {p.is_visible ? (
                            <Badge variant="outline" className="border-success/40 text-success">{t("admin.providers.visible")}</Badge>
                        ) : (
                            <Badge variant="secondary">{t("admin.providers.hidden")}</Badge>
                        )}
                        {p.kyc_submitted_at && (
                            <span className="text-xs text-muted-foreground">
                                {t("kyc.submittedAt", { date: new Date(p.kyc_submitted_at).toLocaleString() })}
                            </span>
                        )}
                    </div>
                </div>
                {/* Submit button kept here as data-testid anchor; visually hidden when banner is shown */}
            </section>

            {/* Ready-to-submit nudge */}
            {data.can_submit && (
                <div className="rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex items-start gap-2 flex-1">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-foreground">{t("kyc.readyTitle")}</p>
                            <p className="text-sm text-muted-foreground">{t("kyc.readyBody")}</p>
                        </div>
                    </div>
                    <Button onClick={onSubmit} disabled={submitting} className="shrink-0">
                        {submitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Send className="h-4 w-4 me-2" />}
                        {t("kyc.submitForReview")}
                    </Button>
                </div>
            )}

            {p.kyc_rejection_reason && (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3" data-testid="kyc-rejection-banner">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-destructive">{t("kyc.rejected")}</p>
                            <p className="mt-1 text-sm">{p.kyc_rejection_reason}</p>
                            <p className="mt-2 text-xs text-muted-foreground">{t("kyc.rejectedHint")}</p>
                        </div>
                    </div>
                </div>
            )}

            {isApproved && (
                <div className="rounded-2xl border border-success/30 bg-success/5 px-4 py-3 inline-flex items-center gap-2" data-testid="kyc-approved-banner">
                    <ShieldCheck className="h-4 w-4 text-success" />
                    <span className="text-sm">{t("kyc.approvedNote")}</span>
                </div>
            )}

            <KycDocumentsPanel
                summary={data}
                readOnly={isPending}
                uploadFn={providerKycApi.upload}
                removeFn={providerKycApi.remove}
                onChange={refresh}
                testIdPrefix="kyc"
            />

            {isPending && (
                <div className="rounded-2xl border border-warning/30 bg-warning/5 px-4 py-3 text-sm" data-testid="kyc-pending-banner">
                    {t("kyc.pendingNote")}
                </div>
            )}
        </div>
    );
}
